import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface SnapshotRow {
  readonly analyticsSnapshotId: string;
  readonly snapshotType: string;
  readonly capturedAt: string;
  readonly status?: string;
}

const SNAPSHOT_TYPES = [
  'DAILY_OPS', 'WEEKLY_FINANCE', 'MONTHLY_MIS', 'QUARTERLY_REVIEW',
  'ANNUAL_SUMMARY', 'VENDOR_PERF', 'PAINTER_PERF', 'SLA_COMPLIANCE',
] as const;

/** L16.10 — Analytics Center (`/reports/analytics`). Captures and lists
 *  analytics snapshots and surfaces painter/branding analytics (M3). */
export function M16AnalyticsCenterPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<SnapshotRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snapshotType, setSnapshotType] = useState<string>('DAILY_OPS');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function load(): Promise<void> {
    setRows(await apiRequest<SnapshotRow[]>('/v1/m16/analytics/snapshots', { token }));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<SnapshotRow[]>('/v1/m16/analytics/snapshots', { token })
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  async function capture(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(`/v1/m16/analytics/snapshots/${snapshotType}`, { method: 'POST', token });
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m16.analytics.loading')}</p>;
  return (
    <main aria-label="analytics-center">
      <h1>{t('m16.analytics.title')}</h1>
      <section aria-label="snapshot-form">
        <select aria-label="snapshot-type" data-testid="snapshot-type"
          value={snapshotType} onChange={(e) => setSnapshotType(e.target.value)}>
          {SNAPSHOT_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
        </select>
        <button type="button" data-testid="capture-snapshot-btn" disabled={busy} onClick={capture}>{t('m16.analytics.capture')}</button>
      </section>
      {actionError && <p role="alert">{actionError}</p>}
      <table aria-label="snapshots-table">
        <thead>
          <tr><th>{t('m16.analytics.colType')}</th><th>{t('m16.analytics.colCaptured')}</th></tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.analyticsSnapshotId}>
              <td>{s.snapshotType}</td>
              <td>{at(s.capturedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
